import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { useAuth } from '../contexts/AuthContext';
import { Users, Trophy, Swords, TrendingUp, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const StatCard = ({ icon: Icon, value, label, color = 'bg-muted', iconColor = 'text-foreground' }) => (
  <div className="bg-card rounded-xl border border-border p-6">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className={`h-6 w-6 ${iconColor}`} />
      </div>
      <div>
        <p className="text-3xl font-bold text-foreground font-heading">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { t, language } = useLanguage();
  const { admin } = useAuth();
  const { ladder, players, matches, leaderboard, loading } = useLadder();

  // Helper for table header class based on language
  const headerClass = language === 'el' 
    ? 'text-xs font-semibold tracking-wider text-muted-foreground'
    : 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-dashboard-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Handle both old (array) and new (object with qualified/unranked) response formats
  const qualifiedPlayers = Array.isArray(leaderboard) ? leaderboard : (leaderboard?.qualified || []);
  const topPlayer = qualifiedPlayers[0];
  const totalGamesPlayed = matches.reduce((acc, m) => {
    return acc + m.sets.reduce((setAcc, s) => setAcc + s.team_a + s.team_b, 0);
  }, 0);

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-foreground mb-1">
          {t('dashboard')}
        </h1>
        <p className="text-muted-foreground">
          {ladder?.name || 'Padel Ladder'} • {new Date().toLocaleDateString('el-GR', { month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          icon={Users} 
          value={players.length} 
          label={t('totalPlayers')}
          color="bg-blue-50 dark:bg-blue-900/30"
          iconColor="text-blue-600 dark:text-blue-400"
        />
        <StatCard 
          icon={Swords} 
          value={matches.length} 
          label={t('totalMatches')}
          color="bg-amber-50 dark:bg-amber-900/30"
          iconColor="text-amber-600 dark:text-amber-400"
        />
        <StatCard 
          icon={Trophy} 
          value={totalGamesPlayed} 
          label="Games Played"
          color="bg-green-50 dark:bg-green-900/30"
          iconColor="text-green-600 dark:text-green-400"
        />
        <StatCard 
          icon={TrendingUp} 
          value={topPlayer?.player_name?.split(' ')[0] || '-'} 
          label="Current Leader"
          color="bg-purple-50 dark:bg-purple-900/30"
          iconColor="text-purple-600 dark:text-purple-400"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-card rounded-xl border border-border p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/admin/players">
              <Button variant="outline" className="w-full justify-start gap-2 h-12">
                <Plus className="h-4 w-4" />
                {t('addPlayer')}
              </Button>
            </Link>
            <Link to="/admin/matches">
              <Button className="w-full justify-start gap-2 h-12 bg-green-600 hover:bg-green-700">
                <Swords className="h-4 w-4" />
                {t('submitMatch')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-4">Recent Matches</h2>
          {matches.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('noMatches')}</p>
          ) : (
            <div className="space-y-3">
              {matches.slice(-3).reverse().map((match, index) => (
                <div key={match.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm text-foreground">
                    {t('teamA')} vs {t('teamB')}
                  </span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    match.winner_team === 'A' 
                      ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                      : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  }`}>
                    {t('teamA')} {match.winner_team === 'A' ? t('wins') : t('losses')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top 5 Leaderboard Preview */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-foreground">Top Players</h2>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            View Full Leaderboard →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className={`px-6 py-3 text-left ${headerClass}`}>
                  {t('rank')}
                </th>
                <th className={`px-6 py-3 text-left ${headerClass}`}>
                  {t('player')}
                </th>
                <th className={`px-6 py-3 text-center ${headerClass}`}>
                  {t('points')}
                </th>
                <th className={`px-6 py-3 text-center ${headerClass}`}>
                  {t('matches')}
                </th>
              </tr>
            </thead>
            <tbody>
              {qualifiedPlayers.slice(0, 5).map((player, index) => (
                <tr key={player.player_id} className="border-b border-border last:border-0">
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold ${
                      index === 0 ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400' :
                      index === 1 ? 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300' :
                      index === 2 ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {player.rank}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-medium text-foreground">{player.player_name}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-bold">
                      {player.total_points}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center text-muted-foreground">{player.matches_played}</td>
                </tr>
              ))}
              {qualifiedPlayers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                    {t('noPlayers')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
